// src/stores/useToastStore.js
//
// App-wide toast notifications. Pure in-house — no library dependency.
// Subscribed by <ToastHost /> mounted at App.jsx root level so toasts
// are reachable from any code path (PadRekko, Platform Admin, future
// modes).
//
// Two APIs:
//
//   import { useToastStore } from "../../stores/useToastStore";
//   useToastStore.getState().showToast({ type, message, durationMs })
//
//   import { toast } from "../../stores/useToastStore";
//   toast.success("Saved");
//   toast.warning("Majority skipped at handoff");
//   toast.error("Stage advanced under you", { sticky: true });
//   toast.info("Background job started", { durationMs: 3000 });
//
// Defaults:
//   success / info : 5000 ms auto-dismiss
//   warning        : 8000 ms auto-dismiss
//   error          : sticky (no auto-dismiss; user clicks X)
//
// Sticky errors are intentional — they usually point at recoverable
// state the user should acknowledge before moving on. Override via
// { sticky: false, durationMs: 5000 } on the toast call if a specific
// error should auto-dismiss.

import { create } from "zustand";

const DEFAULT_DURATIONS = {
  success: 5000,
  info:    5000,
  warning: 8000,
  error:   null, // sticky
};

let nextId = 1;

export const useToastStore = create((set, get) => ({
  // [{ id, type, message, createdAt }]
  toasts: [],

  /**
   * Lower-level API. Returns the new toast id.
   * @param {Object} args
   * @param {'success'|'info'|'warning'|'error'} args.type
   * @param {string} args.message
   * @param {number|null} [args.durationMs]  Override; null = sticky.
   * @param {function} [args.onClick]        Optional click handler — when
   *        set, the toast body (not the X close button) becomes clickable
   *        and invokes this function. Dismisses the toast after running.
   */
  showToast({ type, message, durationMs, onClick }) {
    const id = nextId++;
    const resolvedDuration =
      durationMs === undefined ? DEFAULT_DURATIONS[type] ?? 5000 : durationMs;
    set((s) => ({
      toasts: [...s.toasts, { id, type, message, onClick, createdAt: Date.now() }],
    }));
    if (resolvedDuration !== null && resolvedDuration > 0) {
      setTimeout(() => get().dismissToast(id), resolvedDuration);
    }
    return id;
  },

  dismissToast(id) {
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },

  clearAllToasts() {
    set({ toasts: [] });
  },
}));

// Convenience helpers. Accept an optional second arg for overrides:
//   toast.error("Failed", { sticky: false, durationMs: 3000 })
//   toast.success("Saved", { durationMs: 2000 })
//
// `sticky: true` is equivalent to `durationMs: null`.
function callShow(type, message, options) {
  const { showToast } = useToastStore.getState();
  let durationMs;
  if (options) {
    if (options.sticky === true) durationMs = null;
    else if (typeof options.durationMs === "number") durationMs = options.durationMs;
  }
  return showToast({ type, message, durationMs, onClick: options?.onClick });
}

export const toast = {
  success: (message, options) => callShow("success", message, options),
  warning: (message, options) => callShow("warning", message, options),
  error:   (message, options) => callShow("error",   message, options),
  info:    (message, options) => callShow("info",    message, options),
};
